import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalendarDays,
  Check,
  ChevronDown,
  CircleDashed,
  Flag,
  Tag,
  X,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { api, extractApiError } from '../../lib/api'
import { formatNumber } from '../../lib/format'
import { invalidateGamification } from '../../lib/gamification-refresh'
import { labelsResponse } from '../../lib/types'
import { DateTimePickerField } from '../../components/ui/DateTimePickerField'
import { parseDateInput, datePlaceholder } from '../../components/ui/dateTimeInput'

/**
 * Bulk-edit action bar for the Task Monitor (#6 Bulk edit). Renders once when
 * ≥1 row is selected and drives the tenant-wide `POST /tasks/bulk` endpoint:
 * Priority, Due Date, Labels (add / remove) and Status. The backend checks the
 * actor's permission per task and returns `{ succeeded, failed }`, so every
 * action surfaces a partial-failure summary rather than an all-or-nothing error.
 *
 * Priority / Due Date / Labels are safe across workspaces. Status is
 * per-workspace (each workflow has its own status names), so the Status control
 * is only enabled when the whole selection lives in ONE workspace — its options
 * are then drawn from that workspace's tasks. The backend still validates the
 * target status against each task's workflow and rejects mismatches.
 */

/** Server cap on `POST /tasks/bulk` (bulk.dto.js `ids.max(500)`). */
const MAX_BULK_IDS = 500

const PRIORITY_CHOICES = [
  { value: 'urgent', label: 'Urgent', color: 'var(--color-priority-urgent)' },
  { value: 'high', label: 'High', color: 'var(--color-priority-high)' },
  { value: 'normal', label: 'Medium', color: 'var(--color-priority-normal)' },
  { value: 'low', label: 'Low', color: 'var(--color-priority-low)' },
  { value: null, label: 'Clear priority', color: null },
]

/** A button that toggles a small popover panel, closing on outside click. */
function Dropdown({ icon: Icon, label, disabled, title, children }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (ref.current && ref.current.contains(e.target)) return
      // The date picker renders its calendar in a portal on document.body
      // (role="dialog"); a click there must NOT close this dropdown, or the
      // day-pick unmounts the field before it registers.
      if (e.target?.closest?.('[role="dialog"]')) return
      setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div className="relative" ref={ref}>
      <Button
        size="sm"
        variant="secondary"
        disabled={disabled}
        title={disabled ? title : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        {Icon ? <Icon size={14} /> : null}
        {label}
        <ChevronDown size={14} />
      </Button>
      {open ? (
        <div className="absolute left-0 top-full mt-1 z-30 min-w-[200px] max-h-[320px] overflow-y-auto rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-lg p-1">
          {typeof children === 'function' ? children(() => setOpen(false)) : children}
        </div>
      ) : null}
    </div>
  )
}

function MenuItem({ onClick, swatch, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-left text-sm text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
    >
      {swatch ? (
        <span
          className="h-2.5 w-2.5 rounded-full shrink-0"
          style={{ backgroundColor: swatch }}
          aria-hidden="true"
        />
      ) : null}
      <span className="truncate">{children}</span>
    </button>
  )
}

function MenuHeading({ children }) {
  return (
    <div className="px-2.5 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-ink-muted)]">
      {children}
    </div>
  )
}

export function TaskMonitorBulkBar({ selectedIds, selectedTasks, allTasks, onClear }) {
  const qc = useQueryClient()
  const count = selectedIds.length
  const [dueValue, setDueValue] = useState('')

  // Tenant labels — shares the ['labels'] cache with LabelsPage, so it MUST
  // store the same shape ({ data: [...] }, parsed by labelsResponse) or
  // whichever page reads second crashes on the other's value.
  const labelsQuery = useQuery({
    queryKey: ['labels'],
    queryFn: async () => {
      const res = await api.get('/labels')
      return labelsResponse.parse(res.data)
    },
    staleTime: 60_000,
  })
  // Memoised because `addableLabels` below depends on it: a bare
  // `?? []` hands back a NEW array on every render while the query is
  // unresolved, so that useMemo would never actually memoise.
  const labels = useMemo(() => labelsQuery.data?.data ?? [], [labelsQuery.data])
  const overLimit = count > MAX_BULK_IDS

  // Status is per-workspace. Enable it only when the whole selection shares one
  // workspace, then offer that workspace's status names (from the loaded rows).
  const singleWorkspaceId = useMemo(() => {
    const ids = new Set()
    for (const t of selectedTasks) if (t.workspace?.id) ids.add(String(t.workspace.id))
    return ids.size === 1 ? [...ids][0] : null
  }, [selectedTasks])

  // A label can only be ADDED where it is available: tenant-wide labels
  // anywhere, workspace labels only inside their own workspace (same rule as
  // labels.service.setTaskLabels; the backend enforces it per task too).
  // Removal stays unfiltered so a stray label can still be cleaned up.
  const addableLabels = useMemo(
    () =>
      labels.filter(
        (l) =>
          l.scope === 'tenant' ||
          (singleWorkspaceId && String(l.workspaceId) === singleWorkspaceId),
      ),
    [labels, singleWorkspaceId],
  )

  const statusOptions = useMemo(() => {
    if (!singleWorkspaceId) return []
    const byName = new Map()
    for (const t of allTasks) {
      if (String(t.workspace?.id) !== String(singleWorkspaceId)) continue
      if (t.statusName && !byName.has(t.statusName)) {
        byName.set(t.statusName, {
          name: t.statusName,
          color: t.statusColor ?? '#94a3b8',
          order: typeof t.statusOrder === 'number' ? t.statusOrder : 999,
        })
      }
    }
    return [...byName.values()].sort((a, b) => a.order - b.order)
  }, [allTasks, singleWorkspaceId])

  const mutation = useMutation({
    mutationFn: async ({ op, value, dueDateHasTime }) => {
      const body = { ids: selectedIds, op, value }
      if (dueDateHasTime !== undefined) body.dueDateHasTime = dueDateHasTime
      const res = await api.post('/tasks/bulk', body)
      return res.data
    },
    onSuccess: (res, vars) => {
      const ok = res?.succeeded?.length ?? 0
      const failed = res?.failed?.length ?? 0
      if (ok > 0 && failed === 0) {
        toast.success(`Updated ${ok} task${ok === 1 ? '' : 's'}.`)
      } else if (ok > 0 && failed > 0) {
        toast.warning(
          `Updated ${ok} of ${ok + failed}. ${failed} could not be changed (permission or workflow).`,
        )
      } else {
        const reason = res?.failed?.[0]?.reason
        toast.error(
          reason
            ? `No tasks were updated — ${reason}`
            : 'No tasks were updated.',
        )
      }
      void qc.invalidateQueries({ queryKey: ['admin', 'tasks', 'monitor'] })
      // A bulk status change moves points/leaderboard state, same as the
      // single-task path.
      if (vars.op === 'status') invalidateGamification(qc)
      setDueValue('')
      onClear()
    },
    onError: (err) =>
      toast.error(extractApiError(err).message || 'Bulk update failed.'),
  })

  const busy = mutation.isPending
  const run = (payload) => {
    if (busy || count === 0 || overLimit) return
    mutation.mutate(payload)
  }

  const applyDueDate = (close) => {
    const parsed = parseDateInput(dueValue)
    if (!parsed) {
      toast.error(`Enter a valid date (${datePlaceholder()}).`)
      return
    }
    run({ op: 'dueDate', value: parsed.toISO(), dueDateHasTime: false })
    close?.()
  }

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4 px-4 py-2.5 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-sm">
      <span className="text-sm font-medium text-[color:var(--color-ink-strong)] tabular-nums">
        {formatNumber(count)} selected
      </span>
      {overLimit ? (
        <span className="text-sm text-[color:var(--color-danger)]">
          Select up to {formatNumber(MAX_BULK_IDS)} tasks to apply a bulk action.
        </span>
      ) : null}

      {/* Priority */}
      <Dropdown icon={Flag} label="Priority" disabled={overLimit}>
        {(close) =>
          PRIORITY_CHOICES.map((p) => (
            <MenuItem
              key={p.label}
              swatch={p.color ?? undefined}
              onClick={() => {
                run({ op: 'priority', value: p.value })
                close()
              }}
            >
              {p.label}
            </MenuItem>
          ))
        }
      </Dropdown>

      {/* Due date */}
      <Dropdown icon={CalendarDays} label="Due date" disabled={overLimit}>
        {(close) => (
          <div className="p-2 w-[240px]">
            <DateTimePickerField
              id="bulk-due-date"
              label="Set Due Date"
              dateOnly
              value={dueValue}
              onChange={setDueValue}
            />
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" variant="primary" onClick={() => applyDueDate(close)}>
                <Check size={14} /> Apply
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  run({ op: 'dueDate', value: null })
                  close()
                }}
              >
                Clear Due Date
              </Button>
            </div>
          </div>
        )}
      </Dropdown>

      {/* Labels — add / remove (BE supports addLabel / removeLabel). */}
      <Dropdown icon={Tag} label="Labels" disabled={overLimit}>
        {(close) => (
          <div className="min-w-[220px]">
            {labelsQuery.isLoading ? (
              <div className="px-2.5 py-2 text-sm text-[color:var(--color-ink-muted)]">
                Loading labels…
              </div>
            ) : labels.length === 0 ? (
              <div className="px-2.5 py-2 text-sm text-[color:var(--color-ink-muted)]">
                No labels yet.
              </div>
            ) : (
              <>
                <MenuHeading>Add label</MenuHeading>
                {addableLabels.length === 0 ? (
                  <div className="px-2.5 py-1.5 text-sm text-[color:var(--color-ink-muted)]">
                    No labels available for all selected tasks.
                  </div>
                ) : null}
                {addableLabels.map((l) => (
                  <MenuItem
                    key={`add-${l.id}`}
                    swatch={l.color}
                    onClick={() => {
                      run({ op: 'addLabel', value: l.id })
                      close()
                    }}
                  >
                    {l.name}
                  </MenuItem>
                ))}
                <div className="my-1 border-t border-[color:var(--color-rule)]" />
                <MenuHeading>Remove label</MenuHeading>
                {labels.map((l) => (
                  <MenuItem
                    key={`remove-${l.id}`}
                    swatch={l.color}
                    onClick={() => {
                      run({ op: 'removeLabel', value: l.id })
                      close()
                    }}
                  >
                    {l.name}
                  </MenuItem>
                ))}
              </>
            )}
          </div>
        )}
      </Dropdown>

      {/* Status — workspace-aware */}
      <Dropdown
        icon={CircleDashed}
        label="Status"
        disabled={overLimit || !singleWorkspaceId || statusOptions.length === 0}
        title="Select tasks from a single workspace to bulk-set status."
      >
        {(close) =>
          statusOptions.map((s) => (
            <MenuItem
              key={s.name}
              swatch={s.color}
              onClick={() => {
                run({ op: 'status', value: s.name })
                close()
              }}
            >
              {s.name}
            </MenuItem>
          ))
        }
      </Dropdown>

      <div className="ml-auto flex items-center gap-2">
        {busy ? (
          <span className="text-sm text-[color:var(--color-ink-muted)]">Applying…</span>
        ) : null}
        <Button size="sm" variant="secondary" onClick={onClear} disabled={busy}>
          <X size={14} /> Clear
        </Button>
      </div>
    </div>
  )
}
